import { between } from '@iotv/core-services'
import type { ValidBusinessObject } from '@iotv/iotv-v3-types'
import { isNonNaNNumber, Milliseconds } from '@iotv/iotv-v3-types'
import { v4 } from 'uuid'
import { UserAttributeDefs, UserAttributeKeyDatePrecision } from '../../../types/ModelTypes'
import { DEBUG_OVERRIDE, NOT_LAMBDA } from '../../../util/lambda'
import { getReasonableTimestampSecondsFilter, getReasonableValueNumber } from '../../../util/time/helpers'
import { ModelCommon } from '../../ModelCommon'
import { getCurrentAndNextScheduledInfo, type CurrentAndNextScheduleInfoOutput } from './rollingScheduleFns'
import { EntityRollingProgrammeConstructorProps, IEntityRollingProgramme, RollingPatternConfig } from './types'

/** CRA 2025 05 — TODO: this is bad technical debt, but I don't know how else to do it... */
const commonSupportedTypes = ['Valve', 'WaterCannon', 'Irrigator', 'MasterValve', 'SVCx'].sort((a, b) => a.localeCompare(b))

const supportedTypes = Object.fromEntries(
  [...commonSupportedTypes]
    .filter((x) => x)
    .map((type) => [type, type])
    .sort(),
)

const debug = DEBUG_OVERRIDE || (NOT_LAMBDA && false)

const reasonableSeconds = getReasonableTimestampSecondsFilter(10, 10)
const reasonableWindowcount = getReasonableValueNumber(1, 1000)

export const isEntityRollingProgramme = (x: any): x is EntityRollingProgrammeConstructorProps => {
  const c = x as EntityRollingProgrammeConstructorProps
  const isConstructorProps = isEntityRollingProgrammeConstructorProps(x)
  const isInstance = c instanceof EntityRollingProgramme
  const isEntityProgramme = isConstructorProps && isInstance
  if (c && !isEntityProgramme) {
    ;(c as EntityRollingProgrammeConstructorProps & ValidBusinessObject)?.type === 'EntityProgramme' &&
      console.log(`WARN isEntityProgramme returning false for EntityProgramme ${c.id} with`, { isConstructorProps, isInstance })
  }
  return isEntityProgramme
}

export const isEntityRollingProgrammeConstructorProps = (x: any): x is EntityRollingProgrammeConstructorProps => {
  const { startTimeUtcSeconds, name, windows } = (x as EntityRollingProgrammeConstructorProps) ?? {}
  const isEntityRollingProgrammeConstructorProps =
    typeof name === 'string' && (startTimeUtcSeconds === null || isNonNaNNumber(startTimeUtcSeconds)) && (windows === null || isNonNaNNumber(windows))
  if (!isEntityRollingProgrammeConstructorProps) {
    ;(x as EntityRollingProgrammeConstructorProps & ValidBusinessObject)?.type === 'EntityProgramme' &&
      console.log(`WARN isEntityProgrammeConstructorProps returning false  for EntityProgramme ${x?.id} with`, { name, windows, startTimeUtcSeconds })
  }

  return isEntityRollingProgrammeConstructorProps
}

type CurrentWindowAndSchedule = {
  windowIdx: number
  currentNextInfo: CurrentAndNextScheduleInfoOutput
  rollingPatternConfig: RollingPatternConfig
}

interface IEntityRollingProgrammeFunctions {
  getCurrentWindowAndSchedule: (relativeDate: Date) => CurrentWindowAndSchedule | null
  updateAppliedTimestamps: (timestampMs: number) => void
}

export class EntityRollingProgramme
  extends ModelCommon
  implements IEntityRollingProgramme, EntityRollingProgrammeConstructorProps, IEntityRollingProgrammeFunctions
{
  constructor({
    id = v4(),
    supportedType,
    enabled,
    maxNumberOfEntitiesPerTimeslot,
    name,
    windows,
    startTimeUtcSeconds,
    durationMins,
    windowGapDurationMins,
    cycleGapDurationMins,
    appliedTimestampsMs,
    durationMinsEach,
    continueWindowCount,
    valves,
    mvcDelay,
    ...others
  }: EntityRollingProgrammeConstructorProps) {
    super('EntityRollingProgramme', { id, ...others })
    /* the intention here was to fully enforce reasonablness but arbitrary construction from Assembler will error if this is left to deal with undefined*/
    if (startTimeUtcSeconds && !reasonableSeconds(startTimeUtcSeconds))
      throw new Error(`${startTimeUtcSeconds} ${new Date(startTimeUtcSeconds * Milliseconds.SECOND)} is not a reasonable timestamp in seconds`)
    this.maxNumberOfEntitiesPerTimeslot = maxNumberOfEntitiesPerTimeslot ?? null
    this.name = name
    this.supportedType = supportedType
    this.enabled = enabled ?? true
    this.startTimeUtcSeconds = startTimeUtcSeconds ?? 0
    this.durationMins = durationMins ?? 1
    this.windows = reasonableWindowcount(windows) ? windows : 1
    this.windowGapDurationMins = windowGapDurationMins ?? 1
    this.cycleGapDurationMins = cycleGapDurationMins ?? 1
    this.appliedTimestampsMs = appliedTimestampsMs ?? null
    this.durationMinsEach = durationMinsEach ?? Array(this.windows).fill(null)
    this.continueWindowCount = continueWindowCount ?? null
    this.valves = valves ?? []
    this.mvcDelay = mvcDelay ?? null
  }

  supportedType: string
  enabled: boolean
  maxNumberOfEntitiesPerTimeslot: number | null
  name: string
  windows: number | null
  startTimeUtcSeconds: number | null
  durationMins: number | null
  windowGapDurationMins: number
  cycleGapDurationMins: number
  appliedTimestampsMs: null | number[]
  durationMinsEach?: (number | null)[]
  maxAppliedTimestamps: number = 50
  continueWindowCount?: number | null
  valves?: string[]
  mvcDelay?: number | null

  getDurationToWindow = (windowIdx: number) => {
    const defaultDuration = this.durationMins ?? 0

    const durationUpToWindow: number = this.durationMinsEach
      ? this.durationMinsEach.slice(0, windowIdx).reduce((acc: number, cur) => {
          acc += cur !== null ? cur : defaultDuration
          return acc
        }, 0)
      : defaultDuration * windowIdx

    return durationUpToWindow
  }

  /**
   * Supporting function that returns the duration of that particular window, or the default
   */
  getWindowDuration = (windowIdx?: number): number | null => {
    if (windowIdx === undefined) {
      return this.durationMins ?? null
    }
    return this.durationMinsEach && this.durationMinsEach[windowIdx] ? this.durationMinsEach[windowIdx] : this.durationMins
  }

  /** returns the totol cylce time - time in mins for the gap between cycles and the product of number of windows and ( durationMins + gapBetweenWindowsMins )  */
  getTotalRepeatTimeMins = () => {
    // sum all duration
    if (this.windows && this.durationMins) {
      // duration array is sliced in case the duration array is not maintained as the window count changes
      const nonNullDurations: number[] = this.durationMinsEach ? this.durationMinsEach.slice(0, this.windows).filter((val) => val !== null) : []
      const definedDuration = nonNullDurations.reduce((acc, cur) => {
        acc += cur
        return acc
      }, 0)

      const totalDuration = definedDuration + (this.windows - (nonNullDurations?.length ?? 0)) * this.durationMins

      return totalDuration + this.cycleGapDurationMins + this.windows * this.windowGapDurationMins
    }
    return null
  }
  /** returns the calculated delayMins to apply to each rollingProgammeConfig, based on the number of windows, specified duration, gap between windows and gap between cycles
   * the device enters delay phase first then operation phase (durationMins) so every config incluing the first starts with the calculated delay
   */
  getCalculatedDelayMins = (windowIdx?: number) => {
    const duration = this.getWindowDuration(windowIdx)
    return this.windows && duration ? this.getTotalRepeatTimeMins()! - duration : null
  }

  getUnpausingAdjustment = (windowIdx: number) => {
    let shiftTimeMinutes = 0
    // check should not be required, and should be done before it is called
    if (this.continueWindowCount && this.continueWindowCount > 0) {
      if (windowIdx < this.continueWindowCount) {
        // add time of one complete cycle
        shiftTimeMinutes += this.getTotalRepeatTimeMins() ?? 0
      }

      // shift time such that windowIdx starts 30 minutes from now
      const unshiftAmount = this.getDurationToWindow(this.continueWindowCount)
      shiftTimeMinutes -= unshiftAmount ?? 0
    }

    return shiftTimeMinutes * 60
  }

  /** returns a rollingPatternConfig for any window index */
  getRollingProgrammeConfigForWindowIdx = (windowIdx: number): RollingPatternConfig | null => {
    let res: RollingPatternConfig | null = null
    const {
      // durationMins: genericDuration,
      windows,
      startTimeUtcSeconds: programmeStartTimeUtcSeconds,
      windowGapDurationMins,
      // cycleGapDurationMins,
    } = this
    const delayMins = this.getCalculatedDelayMins(windowIdx)
    const durationMins = this.getWindowDuration(windowIdx)

    if (durationMins && windows && programmeStartTimeUtcSeconds && delayMins) {
      if (between(windowIdx, 0, windows)) {
        // const interCycleTimeMins = durationMins + windowGapDurationMins
        // const startTimeUtcSeconds = programmeStartTimeUtcSeconds + interCycleTimeMins * windowIdx * 60

        // slice is done as we want the duration period up to the current window. If it doesn't exist, use the original logic
        const durationUpToWindow: number = this.getDurationToWindow(windowIdx)
        //  this.durationMinsEach
        //   ? this.durationMinsEach.slice(0, windowIdx).reduce((acc: number, cur) => {
        //       acc += cur !== null ? cur : durationMins
        //       return acc
        //     }, 0)
        //   : durationMins * windowIdx

        const windowGapsDuration: number = windowGapDurationMins * windowIdx
        let startTimeUtcSeconds = programmeStartTimeUtcSeconds + (windowGapsDuration + durationUpToWindow) * 60

        // shift time due to pause
        if (this.continueWindowCount && this.continueWindowCount > 0) {
          const adjustmentTime = this.getUnpausingAdjustment(windowIdx)

          startTimeUtcSeconds += adjustmentTime
        }

        res = {
          delay: delayMins,
          duration: durationMins,
          moistureThreshold: 0,
          startTimeUtcSeconds,
        }
        /** TAG: DISABLED PROGRAMME PROVISION */
        if (this.enabled === false) {
          res.delay = 0
          res.duration = 0
        }
      } else {
        console.log(`WARN getRollingProgrammeConfigForWindowIdx ${windowIdx} is out of bounds, max is ${windows - 1}`)
      }
    } else {
      console.log(`WARN: getRollingProgrammeConfigForWindowIdx failing predicate`, {
        durationMins,
        windows,
        programmeStartTimeUtcSeconds,
        windowGapDurationMins,
      })
    }
    return res
  }

  /** returns current and next info for each window, as an array with each index representing window idx */
  getCurrentAndNextScheduledInfo = (relativeDate: Date) => {
    const arr: (CurrentAndNextScheduleInfoOutput | null)[] = Array.from({ length: this.windows ?? 0 }, (v, i) => {
      const rollingPatternConfig = this.getRollingProgrammeConfigForWindowIdx(i)
      return rollingPatternConfig && getCurrentAndNextScheduledInfo({ relativeDate, rollingPatternConfig })
    })

    return arr
  }

  getOverallProgramme = (): RollingPatternConfig => {
    const windows = this.windows ?? 0
    const duration = this.durationMins ?? 0
    const gap = this.windowGapDurationMins ?? 0
    const cycleGap = this.cycleGapDurationMins ?? 0
    let totalDuration = windows * (duration + gap)

    let individualDuration: number[] | undefined
    if (this.durationMinsEach) {
      individualDuration = this.durationMinsEach.map((val) => (val !== null ? val : duration)) as number[]
      let durationOnly =
        individualDuration.reduce((acc, cur) => {
          acc += cur

          return acc
        }, 0) +
        (individualDuration.length - windows) * duration
      let windowOnly = gap * windows
      totalDuration = durationOnly + windowOnly
    }

    return {
      delay: cycleGap,
      duration: totalDuration,
      moistureThreshold: 0,
      startTimeUtcSeconds: Math.floor(this.startTimeUtcSeconds ?? Date.now() / 1000),
    }
  }

  getMVCProgramme = (): RollingPatternConfig => {
    let overall = this.getOverallProgramme()
    let mvcDelay = this.mvcDelay ?? 0

    const padding = 2 * mvcDelay
    const startTimeUtcSeconds = overall.startTimeUtcSeconds + mvcDelay * 60
    const duration = overall.duration - padding
    const delay = overall.delay + padding
    return {
      moistureThreshold: 0,
      delay,
      duration,
      startTimeUtcSeconds,
    }
  }

  getCurrentWindowAndSchedule = (relativeDate: Date = new Date()) =>
    this.getCurrentAndNextScheduledInfo(relativeDate).reduce(
      (a, currentNextInfo: CurrentAndNextScheduleInfoOutput | null, windowIdx) => {
        if (!a) {
          if (currentNextInfo?.isInDelayPhase === false) {
            const rollingPatternConfig = this.getRollingProgrammeConfigForWindowIdx(windowIdx)
            if (rollingPatternConfig) {
              a = {
                currentNextInfo,
                rollingPatternConfig,
                windowIdx,
              }
            }
          }
        }
        return a
      },
      null as CurrentWindowAndSchedule | null,
    )

  updateAppliedTimestamps = (timestampMs: number) => {
    if (!this.appliedTimestampsMs) {
      this.appliedTimestampsMs = []
    }
    this.appliedTimestampsMs?.unshift(timestampMs)
    this.appliedTimestampsMs = this.appliedTimestampsMs.slice(0, this.maxAppliedTimestamps)
  }

  getUserAttributes(): UserAttributeDefs {
    return {
      ...super.getUserAttributes(),
      supportedType: {
        type: 'Enum',
        editable: true,
        required: true,
        enumKv: supportedTypes,
      },
      enabled: {
        type: 'Boolean',
        editable: true,
        required: true,
        default: true,
      },
      startTimeUtcSeconds: {
        type: 'Date',
        editable: true,
        precision: UserAttributeKeyDatePrecision.DateTimeMins,
        label: 'Start Time',
        displayFn: (v: number) => v * Milliseconds.SECOND,
        valueFn: (v: number) => v / Milliseconds.SECOND,
      },
      windows: {
        type: 'Number',
        editable: true,
        precision: 0,
        label: 'Windows',
        default: 1,
        limits: { min: 1 },
      },
      durationMins: {
        type: 'Number',
        editable: true,
        precision: 0,
        label: 'Duration',
        units: 'mins',
        default: 1,
        limits: { min: 1 },
      },
      windowGapDurationMins: {
        type: 'Number',
        editable: true,
        precision: 0,
        label: 'Window Gap',
        units: 'mins',
        default: 1,
      },
      cycleGapDurationMins: {
        type: 'Number',
        editable: true,
        precision: 0,
        label: 'Cycle Gap',
        units: 'mins',
        default: 1,
      },
    }
  }
}
